<template>
  <div class="membership-page">
    <div class="container">
      <router-link to="/profile" class="back-link">&larr; My Profile</router-link>
      <h1>Membership</h1>

      <Spinner :show="loading" message="Loading your membership..." />

      <template v-if="!loading">
        <!-- Active member: manage -->
        <section v-if="isMember" class="card active-card">
          <div class="status-row">
            <span class="status-pill on">{{ isPaused ? 'Paused' : 'Active' }}</span>
            <span v-if="details && details.plan" class="plan-name">{{ details.plan }}</span>
          </div>
          <p class="lead">Your membership is active. As we roll the assistant out to you, it'll watch your people and reach out on your behalf — and you approve every gift before anything ships.</p>

          <div class="manage-actions">
            <button v-if="!isPaused" class="button ghost" :disabled="busy" @click="pause">Pause</button>
            <button v-else class="button primary" :disabled="busy" @click="resume">Resume</button>
            <button class="button ghost" :disabled="busy" @click="openPortal">Manage billing</button>
            <button class="button danger-text" :disabled="busy" @click="cancel">Cancel membership</button>
          </div>
        </section>

        <!-- Not a member: pitch + subscribe -->
        <section v-else class="card">
          <div class="price-row">
            <span class="price">{{ priceDisplay }}</span>
            <span class="interval">/ {{ interval }}</span>
          </div>
          <p class="lead">Turn on your assistant. It keeps you close to the people who matter — without you having to remember.</p>

          <ul class="benefits">
            <li v-for="(b, i) in benefits" :key="i">
              <span class="check" aria-hidden="true">✓</span> {{ b }}
            </li>
          </ul>

          <button
            class="button primary block"
            :disabled="busy || !configured"
            @click="subscribe"
          >
            {{ busy ? 'Starting checkout…' : `Become a member — ${priceDisplay}/${interval}` }}
          </button>
          <p v-if="!configured" class="config-note">
            Membership isn't available just yet — check back soon.
          </p>
          <p class="fine-print">Cancel anytime. Postage is on us — the mail your assistant sends is included. Only gifts you approve are billed separately, per item.</p>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/SpinnerComp.vue";
// `/pure` so Stripe.js isn't auto-injected on import (the default entrypoint
// loads a 246KB render-blocking script on every page). Loads lazily on the
// first loadStripe() call — i.e. only when the member clicks subscribe.
import { loadStripe } from "@stripe/stripe-js/pure";
import {
  getSubscriptionDetails,
  createSubscriptionSession,
  createCustomerPortalLink,
  cancelSubscription,
  pauseSubscription,
  resumeSubscription,
  verifyCheckoutSession,
} from "@/api";
import {
  MEMBERSHIP_PRICE_ID,
  MEMBERSHIP_PRICE_DISPLAY,
  MEMBERSHIP_PRICE_VALUE,
  MEMBERSHIP_INTERVAL,
  MEMBERSHIP_BENEFITS,
  isMembershipConfigured,
  isActiveMembershipStatus,
} from "@/config/membership";
import { track, USD, MEMBERSHIP_ITEM } from "@/utils/track";

export default {
  name: "MembershipPage",
  components: { Spinner },
  data() {
    return {
      loading: true,
      busy: false,
      details: null,
      priceDisplay: MEMBERSHIP_PRICE_DISPLAY,
      interval: MEMBERSHIP_INTERVAL,
      benefits: MEMBERSHIP_BENEFITS,
      configured: isMembershipConfigured(),
    };
  },
  computed: {
    isMember() {
      return !!this.details && isActiveMembershipStatus(this.details.status);
    },
    isPaused() {
      return !!(this.details && this.details.pause_collection);
    },
  },
  async mounted() {
    // Stripe returns to /membership?session_id=... after a completed checkout
    // (the subscription helper appends the id). Fire the `subscribe` conversion
    // only after the session is verified paid server-side — never on a bare URL
    // param — so a shared/bookmarked link can't log a phantom conversion.
    const sessionId = this.$route.query.session_id;
    if (sessionId && !this.isSessionProcessed(sessionId)) {
      await this.confirmSubscriptionConversion(sessionId);
    }
    await this.loadDetails();
  },
  methods: {
    // Shared with the postcard success flow: a Stripe session id is tracked once
    // so a refresh (or a re-verify) can't double-count the conversion.
    isSessionProcessed(sessionId) {
      try {
        const processed = JSON.parse(sessionStorage.getItem("processedSessions") || "[]");
        return processed.includes(sessionId);
      } catch {
        return false;
      }
    },
    markSessionProcessed(sessionId) {
      try {
        const processed = JSON.parse(sessionStorage.getItem("processedSessions") || "[]");
        if (!processed.includes(sessionId)) {
          processed.push(sessionId);
          sessionStorage.setItem("processedSessions", JSON.stringify(processed));
        }
      } catch {
        sessionStorage.setItem("processedSessions", JSON.stringify([sessionId]));
      }
    },
    async confirmSubscriptionConversion(sessionId) {
      try {
        const { data } = await verifyCheckoutSession({ sessionId });
        if (data && data.success) {
          track("subscribe", {
            transaction_id: sessionId,
            currency: USD,
            value: MEMBERSHIP_PRICE_VALUE,
            items: [{ ...MEMBERSHIP_ITEM, price: MEMBERSHIP_PRICE_VALUE, quantity: 1 }],
          });
          this.markSessionProcessed(sessionId);
        }
      } catch (e) {
        // Verification failed — skip the event. Telemetry is best-effort.
      } finally {
        // Strip session_id so a refresh can't re-verify / re-fire.
        const query = { ...this.$route.query };
        delete query.session_id;
        this.$router.replace({ query });
      }
    },
    async loadDetails() {
      this.loading = true;
      try {
        const res = await getSubscriptionDetails();
        this.details = (res && res.data && res.data.subscriptionDetails) || null;
      } catch (e) {
        this.details = null;
      } finally {
        this.loading = false;
      }
    },
    async subscribe() {
      if (!this.configured) return;
      this.busy = true;
      track("begin_subscription", {
        currency: USD,
        value: MEMBERSHIP_PRICE_VALUE,
        items: [{ ...MEMBERSHIP_ITEM, price: MEMBERSHIP_PRICE_VALUE, quantity: 1 }],
      });
      try {
        const { data } = await createSubscriptionSession({
          // No query string here: the backend helper appends
          // `?session_id={CHECKOUT_SESSION_ID}`, which we read on return to
          // verify the checkout. A query here would produce a malformed `?a?b`.
          successUrl: window.location.origin + "/membership",
          cancelUrl: window.location.origin + "/membership?status=canceled",
          priceId: MEMBERSHIP_PRICE_ID,
        });
        const stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
        if (!stripe) throw new Error("Stripe failed to load");
        // redirectToCheckout resolves (doesn't throw) with {error} on failure.
        const { error } = await stripe.redirectToCheckout({ sessionId: data.id });
        if (error) throw error;
      } catch (e) {
        this.$toast.error("Couldn't start checkout — please try again.");
        this.busy = false;
      }
    },
    async openPortal() {
      this.busy = true;
      try {
        const { data } = await createCustomerPortalLink({
          returnUrl: window.location.origin + "/membership",
        });
        window.location.href = data.url;
      } catch (e) {
        this.$toast.error("Couldn't open billing — please try again.");
        this.busy = false;
      }
    },
    async cancel() {
      this.busy = true;
      try {
        await cancelSubscription();
        this.$toast.success("Membership canceled.");
        await this.loadDetails();
      } catch (e) {
        this.$toast.error("Couldn't cancel — please try again.");
      } finally {
        this.busy = false;
      }
    },
    async pause() {
      this.busy = true;
      try {
        await pauseSubscription();
        this.$toast.success("Membership paused.");
        await this.loadDetails();
      } catch (e) {
        this.$toast.error("Couldn't pause — please try again.");
      } finally {
        this.busy = false;
      }
    },
    async resume() {
      this.busy = true;
      try {
        await resumeSubscription();
        this.$toast.success("Membership resumed.");
        await this.loadDetails();
      } catch (e) {
        this.$toast.error("Couldn't resume — please try again.");
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.membership-page { padding: 24px 16px 64px; }
.container { max-width: 560px; margin: 0 auto; }

.back-link { display: inline-block; color: var(--hollywood-cerise); text-decoration: none; font-size: 0.95rem; font-weight: 500; margin-bottom: 8px; }
.back-link:hover { color: var(--violet-lush); }
.membership-page h1 { color: var(--rich-black); margin: 0 0 24px; }

.card { background: #fff; border: 1px solid #eee; border-radius: 16px; padding: 28px; box-shadow: 0 4px 16px rgba(0,0,0,0.05); }
.active-card { border-color: #bfe6cd; }

.price-row { display: flex; align-items: baseline; gap: 6px; }
.price { font-size: 2.4rem; font-weight: 800; color: var(--rich-black); }
.interval { color: #888; font-weight: 600; }

.status-row { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.status-pill { font-size: 0.75rem; font-weight: 700; padding: 3px 10px; border-radius: 12px; text-transform: uppercase; letter-spacing: 0.03em; }
.status-pill.on { background: #e6f7ec; color: #1e7a44; }
.plan-name { color: #666; font-weight: 600; }

.lead { color: #555; line-height: 1.5; margin: 12px 0 20px; }

.benefits { list-style: none; padding: 0; margin: 0 0 24px; display: flex; flex-direction: column; gap: 12px; }
.benefits li { display: flex; align-items: flex-start; gap: 10px; color: var(--rich-black); font-size: 0.95rem; line-height: 1.4; }
.check { flex: 0 0 20px; width: 20px; height: 20px; border-radius: 50%; background: #e6f7ec; color: #1e7a44; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 700; }

.button { padding: 12px 22px; border: none; border-radius: 10px; font-size: 0.95rem; font-weight: 600; cursor: pointer; text-decoration: none; }
.button.primary { background: var(--accent2-color); color: #fff; }
.button.primary:hover { background: var(--accent-color); }
.button.primary:disabled { opacity: 0.5; cursor: not-allowed; }
.button.block { display: block; width: 100%; text-align: center; }
.button.ghost { background: #f2f2f2; color: var(--rich-black); }
.button.ghost:hover { background: #e8e8e8; }
.button.danger-text { background: none; color: #b3261e; }
.button.danger-text:hover { text-decoration: underline; }
.button:disabled { opacity: 0.5; cursor: not-allowed; }

.manage-actions { display: flex; flex-wrap: wrap; gap: 10px; }

.fine-print { color: #999; font-size: 0.8rem; margin: 14px 0 0; text-align: center; }
.config-note { color: #9a6700; font-size: 0.85rem; margin: 10px 0 0; text-align: center; }
</style>
